import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { Policies } from '@app/auth/auth-policies';
import { AuthService } from '@app/auth/auth.service';
import { ClinicForm } from '@models/forms/clinic-form';
import { FormType, formTypeNames } from '@models/forms/form-type.enum';
import { SharedForm } from '@models/forms/shared-form';
import { GridDataResult, SelectionEvent } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { ActionPanelService, MasterOverlayService } from '@services/actionpanel.service';
import { SharedFormService } from '@services/shared-form.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-form-library-overview',
  templateUrl: './form-library-overview.component.html',
  styleUrls: ['./form-library-overview.component.less'],
})
export class FormLibraryOverviewComponent implements OnInit, OnDestroy {
  private unsub: Subject<void> = new Subject<void>();
  loading = false;
  initialized = false;
  disableGrid = false;
  editMode = false;
  isDeveloper = false;

  FormType = FormType;
  formTypeNames = formTypeNames;
  activeTab: FormType;
  developerPolicy = Policies.developer;

  forms: SharedForm[];
  selectedForm: SharedForm;
  editingForm: SharedForm;
  selectedKeys: number[] = [];
  formRefreshed$ = new Subject<SharedForm>();

  gridView: GridDataResult;
  gridState: State = {
    sort: [],
    skip: 0,
    take: 10,
    filter: {
      logic: 'and',
      filters: [],
    },
  };

  constructor(
    private router: Router,
    private masterOverlayService: MasterOverlayService,
    private actionPanelService: ActionPanelService,
    private sharedFormService: SharedFormService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.isDeveloper = this.authService.userSatisfiesPolicy(Policies.developer);
    this.selectTab(FormType.Consent);
    setTimeout(() => {
      this.initialized = true;
    });
  }

  selectTab(formType: FormType) {
    this.loadForms(formType);
    this.activeTab = formType;
    this.selectedForm = null;
  }

  loadForms(formType?: FormType | null) {
    this.loading = true;
    this.disableGrid = true;

    this.sharedFormService.getSharedFormsByFormType(formType).subscribe((forms) => {
      this.forms = forms;

      this.gridView = {
        data: forms.slice(),
        total: forms.length,
      };

      this.loading = false;
      this.disableGrid = false;
    });
  }

  onSelectRow(e: SelectionEvent) {
    this.editMode = false;
    if (e.selectedRows.length > 0) {
      this.selectRow(e.selectedRows[0].dataItem);
    }
  }

  private selectRow(dataItem: SharedForm) {
    this.selectedForm = dataItem;
    this.formRefreshed$.next(this.selectedForm);
    if (this.selectedKeys.length > 0) {
      this.selectedKeys = [];
    }
    this.selectedKeys.push(this.selectedForm.id);
  }

  showDetails(dataItem: SharedForm): boolean {
    return dataItem.sharedByClinic != undefined && dataItem.sharedByUser !== undefined;
  }

  addFromLibraryHandler(formId: number) {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '325px',
      data: {
        title: 'Add Shared Form?',
        content: 'Are you sure you want to add this Form to your collection?',
        confirmButtonText: 'Add',
        showCancel: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        if (result === 'confirm') {
          this.loading = true;
          this.sharedFormService.addFromLibrary(formId).subscribe((newForm: ClinicForm) => {});
        }
      });
  }

  public closeFormLibrary() {
    this.masterOverlayService.masterOverlayState(false);
    const returnURL = this.router.url.slice(0, this.router.url.indexOf('('));
    this.router.navigate([returnURL, { outlets: { 'action-panel-secondary': null } }]);

    this.actionPanelService.actionPanelClosed.next();
  }

  editMetadataHandler(dataItem: SharedForm) {
    this.editMode = false;
    this.selectRow(dataItem);
    this.router.navigate([
      '/management/forms',
      { outlets: { 'action-panel': ['edit-shared-form', dataItem.id] } },
    ]);
  }

  editFormHandler(dataItem: SharedForm, event?: Event) {
    event?.stopPropagation();
    this.disableGrid = true;
    this.editMode = true;
    this.editingForm = structuredClone(dataItem);
    this.selectRow(dataItem);
  }

  deleteFormHandler(dataItem: SharedForm) {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Shared Form?',
        content: 'Are you sure you want to permanently delete this shared form from the library? This action cannot be undone.',
        confirmButtonText: 'Delete',
        showCancel: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        if (result === 'confirm') {
          this.loading = true;
          this.sharedFormService.deleteSharedForm(dataItem.id).subscribe(() => {
            this.loadForms(this.activeTab);
            this.selectedForm = null;
            this.selectedKeys = [];
            this.loading = false;
          });
        }
      });
  }

  saveHandler(): void {
    const form = this.editingForm;
    this.sharedFormService
      .updateSharedForm(form)
      .pipe(takeUntil(this.unsub))
      .subscribe((updatedForm) => {
        this.selectedForm = updatedForm;
        this.editMode = false;
        this.disableGrid = false;
        this.loadForms(this.activeTab);
      });
  }

  cancelHandler() {
    this.editMode = false;
    this.disableGrid = false;
  }

  public formLibraryOpen(event: TransitionEvent) {
    if (event && event.propertyName && event.propertyName == 'width') {
      this.actionPanelService.actionPanelCompleteTransition(true);
    }
  }

  ngOnDestroy() {
    this.masterOverlayService.masterOverlayState(false);
    this.unsub.next();
    this.unsub.complete();
  }
}
